import React from "react";
import { MoreVertical, Pencil, Trash2 } from "lucide-react";
import { Combobox } from "../../../ui/combobox";
import { Input } from "../../../ui/input";
import { Button } from "../../../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../../ui/dropdown-menu";
import {
  EQUIPMENT_OPTIONS_WITH_CATEGORIES,
  EQUIPMENT_PRESETS,
} from "../../../../config/equipment";
import { chip } from "./MusclesDetail";

const sameKit = (a, b) =>
  Array.isArray(a) &&
  Array.isArray(b) &&
  a.length === b.length &&
  a.every((x) => b.includes(x));

const KitDetail = ({
  locationOptions,
  location,
  onLocationChange,
  equipment,
  onEquipmentChange,
  isAuthenticated,
  equipmentPresets = [],
  presetForm,
  onPresetName,
  onSavePreset,
  onCancelPreset,
  onSaveAsPreset,
  onEditPreset,
  onDeletePreset,
}) => {
  const kit = Array.isArray(equipment) ? equipment : [];
  const locations = locationOptions.filter((o) => !o.disabled);

  return (
    <>
      <div className="space-y-2">
        <p className="text-xs font-semibold text-muted-foreground">Where</p>
        <div className="flex flex-wrap gap-1.5">
          {locations.map((o) => (
            <button
              key={o.value}
              type="button"
              aria-pressed={location === o.value}
              onClick={() => onLocationChange(o.value)}
              className={chip(location === o.value)}
            >
              {o.label}
            </button>
          ))}
        </div>
        <p className="text-xs leading-snug text-muted-foreground">
          Picking a place loads its kit. Adjust it below.
        </p>
      </div>

      <div className="space-y-2">
        <div className="flex items-baseline justify-between gap-2">
          <p className="text-xs font-semibold text-muted-foreground">Kit sets</p>
          {isAuthenticated && !presetForm && (
            <button
              type="button"
              onClick={onSaveAsPreset}
              className="text-xs font-medium text-primary/90 hover:text-primary"
            >
              Save current kit
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-1.5">
          {isAuthenticated &&
            equipmentPresets.map((ep) => (
              <span key={`user-${ep.id}`} className="inline-flex">
                <button
                  type="button"
                  aria-pressed={sameKit(ep.equipment, kit)}
                  onClick={() =>
                    onEquipmentChange(Array.isArray(ep.equipment) ? [...ep.equipment] : [])
                  }
                  className={`${chip(sameKit(ep.equipment, kit))} rounded-r-none`}
                >
                  {ep.name}
                </button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      aria-label={`More for ${ep.name}`}
                      className="-ml-px rounded-r-full border border-border bg-background/40 px-1.5 text-muted-foreground hover:text-foreground"
                    >
                      <MoreVertical className="h-3.5 w-3.5" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="z-[70]">
                    <DropdownMenuItem onClick={() => onEditPreset(ep)}>
                      <Pencil className="mr-2 h-3 w-3" /> Rename or update
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="text-destructive focus:bg-destructive/10 focus:text-destructive"
                      onClick={() => onDeletePreset(ep.id)}
                    >
                      <Trash2 className="mr-2 h-3 w-3" /> Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </span>
            ))}
          {Object.entries(EQUIPMENT_PRESETS).map(([name, list]) => (
            <button
              key={name}
              type="button"
              aria-pressed={sameKit(list, kit)}
              onClick={() => onEquipmentChange([...list])}
              className={chip(sameKit(list, kit))}
            >
              {name}
            </button>
          ))}
        </div>
        {presetForm && (
          <div className="flex items-center gap-2 rounded-xl border border-primary/30 bg-background/40 p-2">
            <Input
              value={presetForm.name}
              onChange={(e) => onPresetName(e.target.value)}
              placeholder="Name this kit set"
              className="h-9 min-w-0 flex-1 bg-background/60 text-sm"
              autoFocus
            />
            <Button type="button" size="sm" className="h-9" disabled={!presetForm.name.trim()} onClick={onSavePreset}>
              Save
            </Button>
            <Button type="button" size="sm" variant="ghost" className="h-9" onClick={onCancelPreset}>
              Cancel
            </Button>
          </div>
        )}
      </div>

      <div className="space-y-2">
        <p className="text-xs font-semibold text-muted-foreground">
          Available today
        </p>
        <Combobox
          multiple
          options={EQUIPMENT_OPTIONS_WITH_CATEGORIES}
          value={kit}
          onChange={onEquipmentChange}
          renderChips
          placeholder="Search kit to add..."
          className="w-full bg-background/50 text-sm"
        />
        {kit.length === 0 && (
          <p className="text-xs text-muted-foreground">Bodyweight only.</p>
        )}
      </div>
    </>
  );
};

export default KitDetail;
